<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type HibpSettings,
    type HibpSettingsRequest,
    type CustomerUser,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // HIBP integration fields
  const isHibpEnabled = ref(false)
  const hibpApiKey = ref('')
  const originalHibpApiKey = ref('')
  const activeBreaches = ref<string[]>([])

  // Available breach types
  const breachTypes = ['Breach', 'Paste', 'Sensitive Data Exposure']

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description:
        'Configure Have I Been Pwned (HIBP) integration settings. This allows JUPUS to check if email addresses have been involved in data breaches.',
      enableHibp: 'Enable Have I Been Pwned Integration',
      apiKey: 'API Key',
      apiKeyPlaceholder: 'Enter HIBP API key',
      activeBreaches: 'Active Breach Types',
      activeBreachesHelper: 'Select which types of breaches to monitor',
      breach: 'Breach',
      paste: 'Paste',
      sensitiveDataExposure: 'Sensitive Data Exposure'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description:
        'Konfigurieren Sie die Have I Been Pwned (HIBP) Integrationseinstellungen. Damit kann JUPUS prüfen, ob E-Mail-Adressen in Datenlecks involviert waren.',
      enableHibp: 'Have I Been Pwned Integration aktivieren',
      apiKey: 'API Key',
      apiKeyPlaceholder: 'HIBP API Key eingeben',
      activeBreaches: 'Aktive Breach-Typen',
      activeBreachesHelper: 'Wählen Sie aus, welche Arten von Breaches überwacht werden sollen',
      breach: 'Breach',
      paste: 'Paste',
      sensitiveDataExposure: 'Sensible Datenexposition'
    }
  })

  const getBreachLabel = (breach: string): string => {
    const labels: Record<string, string> = {
      Breach: t('breach'),
      Paste: t('paste'),
      'Sensitive Data Exposure': t('sensitiveDataExposure')
    }
    return labels[breach] || breach
  }

  const toggleBreach = (breach: string) => {
    const index = activeBreaches.value.indexOf(breach)
    if (index === -1) {
      activeBreaches.value = [...activeBreaches.value, breach]
    } else {
      activeBreaches.value = activeBreaches.value.filter((b) => b !== breach)
    }
  }

  const isBreachActive = (breach: string): boolean => {
    return activeBreaches.value.includes(breach)
  }

  const updateHibpSettings = async () => {
    try {
      isLoading.value = true

      const data: HibpSettingsRequest = {
        is_haveibeenpwned_integration_enabled: isHibpEnabled.value,
        haveibeenpwned_active_breaches: activeBreaches.value
      }

      // Only include API key if it has changed from the original
      if (hibpApiKey.value !== originalHibpApiKey.value) {
        data.haveibeenpwned_api_key = hibpApiKey.value
      }

      await SettingsService.settingsHibpCreate({
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const settings = (await SettingsService.settingsHibpList()) as HibpSettings

      if (settings) {
        isHibpEnabled.value = settings.is_haveibeenpwned_integration_enabled || false
        hibpApiKey.value = settings.haveibeenpwned_api_key || ''
        originalHibpApiKey.value = settings.haveibeenpwned_api_key || ''

        // Handle active breaches - could be array or other format
        if (Array.isArray(settings.haveibeenpwned_active_breaches)) {
          activeBreaches.value = settings.haveibeenpwned_active_breaches as string[]
        } else {
          activeBreaches.value = []
        }
      }
    } catch (error) {
      console.error('Error loading HIBP settings:', error)
      Sentry.captureException(error)
    }
  })

  watch([isHibpEnabled], () => {
    if (user.value) {
      updateHibpSettings()
    }
  })

  watch(hibpApiKey, () => {
    if (user.value) {
      updateHibpSettings()
    }
  })

  watch(
    activeBreaches,
    () => {
      if (user.value) {
        updateHibpSettings()
      }
    },
    { deep: true }
  )
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <p class="mb-4 text-sm text-gray-600">{{ t('description') }}</p>

        <div class="max-w-xl space-y-4">
          <CheckboxField
            v-model="isHibpEnabled"
            name="is_haveibeenpwned_integration_enabled"
            :label="t('enableHibp')"
            data-cy="hibp-integration-enabled-checkbox"
          />

          <div v-if="isHibpEnabled">
            <InputField
              v-model="hibpApiKey"
              name="haveibeenpwned_api_key"
              :label="t('apiKey')"
              :placeholder="t('apiKeyPlaceholder')"
              type="password"
              data-cy="hibp-api-key-input"
            />

            <div class="mt-4">
              <label class="mb-2 block text-sm font-medium text-gray-700">
                {{ t('activeBreaches') }}
              </label>
              <p class="mb-2 text-xs text-gray-500">{{ t('activeBreachesHelper') }}</p>
              <div class="space-y-2">
                <div
                  v-for="breach in breachTypes"
                  :key="breach"
                  class="flex items-center"
                >
                  <input
                    :id="`breach-${breach}`"
                    type="checkbox"
                    :checked="isBreachActive(breach)"
                    class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    @change="toggleBreach(breach)"
                  />
                  <label
                    :for="`breach-${breach}`"
                    class="ml-2 text-sm text-gray-700"
                  >
                    {{ getBreachLabel(breach) }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
